// A board of the call (ADR-0007) as a model, without the page: its shapes; the acts that change them — the
// server's, in its order, and the user's own not back yet, on top; what undoes the user's acts; what lies
// under a point; and drawing it on a canvas. The call's boards (boards.ts) keep these, the page
// (ui/Board.tsx) draws them, and test/board_check.mjs runs this under Node.
import type { ClientMsg, Server } from "./protocol.ts";

/** A shape on a board, in its units (`WIDTH` × `HEIGHT`): what its points are, protocol.xq says. */
export type Shape = Server<"boardState">["shapes"][number];
export type Kind = Shape["kind"];
/** What is done to a board (protocol.xq, `BoardAct`). */
export type Act = Extract<ClientMsg, { type: "board" }>["act"];
/** Who draws on a board: everyone in the call, or the host and moderators. */
export type Mode = "all" | "hosts";

/** A board's units, and what the server takes (protocol.xq: `BOARD_WIDTH`, `MOST_PEN`, …; board.xq's
 * shapes added at once). */
export const WIDTH = 1920;
export const HEIGHT = 1080;
export const MOST_SHAPES = 3000;
export const MOST_PEN = 2000;
export const MOST_TEXT = 500;
export const MOST_ADDED = 1000;
/** How long an act of the user's waits to come back before it is given up, ms: the server lost it. */
export const LOST = 10000;
/** The most steps the user undoes. */
const STEPS = 100;

/** An act of the user's, sent and not back yet: its nonce, the act and when it went (ms). */
export type Pending = { nonce: number; act: Act; at: number };

/** An act of the user's to undo, and to do again: what to send for each. */
export type Step = { undo: Act[]; redo: Act[] };

/** Where another member's pointer is on a board, and when it moved there (ms). */
export type Pointer = { x: number; y: number; at: number };

/**
 * A board as the page knows it: its number, author and who draws; its shapes as the server's acts left
 * them, the lowest first, and the number of its last act; the user's acts not back yet, in the order they
 * went; the pointers of others, by user; the user's steps to undo — the last last — and to do again.
 */
export type Board = {
  number: number;
  author: number;
  mode: Mode;
  shapes: Shape[];
  seq: number;
  pending: Pending[];
  pointers: Map<number, Pointer>;
  undo: Step[];
  redo: Step[];
};

export function newBoard(number: number, author: number, mode: Mode): Board {
  return { number, author, mode, shapes: [], seq: 0, pending: [], pointers: new Map(), undo: [], redo: [] };
}

/** `shapes` after act `a` of user `by`, a new array — or the same, if the act changes no shape (opening,
 * closing, who draws, a pointer). A shape added again is not added twice; what is not there is not moved
 * or erased. */
export function applied(shapes: Shape[], a: Act, by: number): Shape[] {
  switch (a.type) {
    case "add": {
      const ids = new Set(shapes.map((h) => h.id));
      const fresh = a.shapes.filter((h) => !ids.has(h.id)).map((h) => (h.by === 0 ? { ...h, by } : h));
      return fresh.length > 0 ? shapes.concat(fresh) : shapes;
    }
    case "extend":
      return shapes.map((h) => (h.id === a.id ? { ...h, points: joined(h.points, a.points) } : h));
    case "move": {
      const ids = new Set(a.ids);
      return shapes.map((h) => (ids.has(h.id) ? { ...h, points: shifted(h.points, a.dx, a.dy) } : h));
    }
    case "erase": {
      const ids = new Set(a.ids);
      return shapes.filter((h) => !ids.has(h.id));
    }
    case "clear":
      return [];
    default:
      return shapes;
  }
}

/** The shapes the page shows: the server's, with the user's acts not back yet on top. */
export function shown(b: Board, me: number): Shape[] {
  return b.pending.reduce((s, p) => applied(s, p.act, me), b.shapes);
}

/** Board `b` after the server's act `a` — its number `seq` — of user `user`, whose nonce was `nonce`: the
 * user's own leaves the acts not back yet; a pointer moves, or goes (-1). */
export function done(b: Board, a: Act, seq: number, user: number, nonce: number, me: number, now: number): Board {
  if (a.type === "pointer") {
    if (user === me) return b;
    const pointers = new Map(b.pointers);
    if (a.x < 0) pointers.delete(user);
    else pointers.set(user, { x: a.x, y: a.y, at: now });
    return { ...b, pointers };
  }
  const i = user === me ? b.pending.findIndex((p) => p.nonce === nonce) : -1;
  const pending = i < 0 ? b.pending : b.pending.filter((_, k) => k !== i);
  return {
    ...b,
    shapes: applied(b.shapes, a, user),
    seq,
    pending,
    mode: a.type === "mode" ? a.mode : b.mode,
  };
}

/** Board `b` with the server's copy of its shapes from place `from` on, as they are after act `seq`: from
 * 0 the board anew — and the acts not back yet are given up: they went before the copy, on a connection
 * that is gone. */
export function copied(b: Board, from: number, shapes: Shape[], seq: number): Board {
  return from === 0 ? { ...b, shapes, seq, pending: [] } : { ...b, shapes: b.shapes.concat(shapes), seq };
}

/** Board `b` without act `nonce` of the user's, which the server refused. */
export function refused(b: Board, nonce: number): Board {
  return { ...b, pending: b.pending.filter((p) => p.nonce !== nonce) };
}

/** Board `b` without the user's acts that did not come back in `LOST` ms, and the pointers that did not
 * move for `still` ms; the same board if there are none. */
export function aged(b: Board, now: number, still: number): Board {
  const pending = b.pending.filter((p) => now - p.at < LOST);
  const gone = [...b.pointers].filter(([, p]) => now - p.at >= still);
  if (pending.length === b.pending.length && gone.length === 0) return b;
  const pointers = new Map(b.pointers);
  for (const [u] of gone) pointers.delete(u);
  return { ...b, pending, pointers };
}

/** Board `b` with step `s` of the user's to undo, the newest; what was undone is not done again. */
export function stepped(b: Board, s: Step): Board {
  return { ...b, undo: b.undo.concat(s).slice(-STEPS), redo: [] };
}

/** What undoes adding `shapes`, and does it again. */
export function addStep(shapes: Shape[]): Step {
  return { undo: [{ type: "erase", ids: shapes.map((h) => h.id) }], redo: [{ type: "add", shapes }] };
}

/** What undoes erasing `shapes` — they come back as they were — and does it again. */
export function eraseStep(shapes: Shape[]): Step {
  return {
    undo: chunks(shapes, MOST_ADDED).map((part) => ({ type: "add", shapes: part }) as Act),
    redo: [{ type: "erase", ids: shapes.map((h) => h.id) }],
  };
}

/** What undoes moving shapes `ids` by `dx`, `dy`, and does it again. */
export function moveStep(ids: string[], dx: number, dy: number): Step {
  return { undo: [{ type: "move", ids, dx: -dx, dy: -dy }], redo: [{ type: "move", ids, dx, dy }] };
}

function chunks<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

/** The points of `a`, then those of `b`. */
export function joined(a: Int16Array, b: Int16Array): Int16Array {
  const out = new Int16Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/** Points `p` moved by `dx`, `dy`. */
export function shifted(p: Int16Array, dx: number, dy: number): Int16Array {
  return p.map((v, i) => (i % 2 === 0 ? v + dx : v + dy));
}

// ---------------------------------------------------------------- what is where

/** The box of shape `h`: its left, top, right and bottom, its lines' width taken in. */
export function bounds(h: Shape): [number, number, number, number] {
  if (h.kind === "text") return textBox(h);
  const p = h.points;
  let [x0, y0, x1, y1] = [p[0], p[1], p[0], p[1]];
  for (let i = 2; i + 1 < p.length; i += 2) {
    x0 = Math.min(x0, p[i]);
    x1 = Math.max(x1, p[i]);
    y0 = Math.min(y0, p[i + 1]);
    y1 = Math.max(y1, p[i + 1]);
  }
  const r = h.width / 2;
  return [x0 - r, y0 - r, x1 + r, y1 + r];
}

/** About where a text lies: its lines, each `size` high and some 0.6 of it wide a character (Inter). */
export function textBox(h: Shape): [number, number, number, number] {
  const lines = h.text.split("\n");
  const longest = Math.max(1, ...lines.map((l) => [...l].length));
  const [x, y] = [h.points[0], h.points[1]];
  return [x, y, x + longest * h.size * 0.6, y + lines.length * h.size * LEADING];
}

/** The height of a line of text, a text's size each. */
export const LEADING = 1.25;

/** The shape of `shapes` on top at point `x`, `y` — within `tol` of its line, or in a text — or `null`. */
export function hit(shapes: Shape[], x: number, y: number, tol: number): Shape | null {
  for (let i = shapes.length - 1; i >= 0; i--) if (near(shapes[i], x, y, tol)) return shapes[i];
  return null;
}

/** Whether point `x`, `y` is within `tol` of the line of shape `h` — in it, for a text. */
export function near(h: Shape, x: number, y: number, tol: number): boolean {
  const p = h.points;
  const r = h.width / 2 + tol;
  switch (h.kind) {
    case "pen":
      if (p.length === 2) return Math.hypot(x - p[0], y - p[1]) <= r;
      for (let i = 0; i + 3 < p.length; i += 2)
        if (toSegment(x, y, p[i], p[i + 1], p[i + 2], p[i + 3]) <= r) return true;
      return false;
    case "line":
    case "arrow":
      return toSegment(x, y, p[0], p[1], p[2], p[3]) <= r;
    case "rect": {
      const [x0, x1] = [Math.min(p[0], p[2]), Math.max(p[0], p[2])];
      const [y0, y1] = [Math.min(p[1], p[3]), Math.max(p[1], p[3])];
      const inside = x >= x0 && x <= x1 && y >= y0 && y <= y1;
      const edge = inside
        ? Math.min(x - x0, x1 - x, y - y0, y1 - y)
        : Math.hypot(Math.max(x0 - x, 0, x - x1), Math.max(y0 - y, 0, y - y1));
      return edge <= r;
    }
    case "ellipse": {
      const [cx, cy] = [(p[0] + p[2]) / 2, (p[1] + p[3]) / 2];
      const [rx, ry] = [Math.abs(p[2] - p[0]) / 2, Math.abs(p[3] - p[1]) / 2];
      if (rx < 1 || ry < 1) return toSegment(x, y, p[0], p[1], p[2], p[3]) <= r;
      // How far from the outline, roughly: the point's distance from the centre against the outline's
      // in the same direction.
      const t = Math.hypot((x - cx) / rx, (y - cy) / ry);
      return Math.abs(t - 1) * Math.min(rx, ry) <= r;
    }
    case "text": {
      const [x0, y0, x1, y1] = textBox(h);
      return x >= x0 - tol && x <= x1 + tol && y >= y0 - tol && y <= y1 + tol;
    }
  }
}

/** The distance of point `x`, `y` from the segment from `ax`, `ay` to `bx`, `by`. */
function toSegment(x: number, y: number, ax: number, ay: number, bx: number, by: number): number {
  const [dx, dy] = [bx - ax, by - ay];
  const l = dx * dx + dy * dy;
  const t = l === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l));
  return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
}

// ---------------------------------------------------------------- drawing

/** A color as CSS writes it: `#rrggbb`. */
export function css(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/** Draws shapes `shapes` on `ctx`, whose pixels are a board's units times `k`. */
export function paint(ctx: CanvasRenderingContext2D, shapes: Shape[], k: number): void {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const h of shapes) paintShape(ctx, h, k);
}

/** Draws shape `h`. */
export function paintShape(ctx: CanvasRenderingContext2D, h: Shape, k: number): void {
  const p = h.points;
  ctx.strokeStyle = ctx.fillStyle = css(h.color);
  ctx.lineWidth = h.width * k;
  switch (h.kind) {
    case "pen":
      return pen(ctx, p, k, h.width);
    case "line":
    case "arrow": {
      ctx.beginPath();
      ctx.moveTo(p[0] * k, p[1] * k);
      ctx.lineTo(p[2] * k, p[3] * k);
      ctx.stroke();
      if (h.kind === "arrow") head(ctx, p[0] * k, p[1] * k, p[2] * k, p[3] * k, h.width * k);
      return;
    }
    case "rect":
      ctx.strokeRect(
        Math.min(p[0], p[2]) * k,
        Math.min(p[1], p[3]) * k,
        Math.abs(p[2] - p[0]) * k,
        Math.abs(p[3] - p[1]) * k,
      );
      return;
    case "ellipse":
      ctx.beginPath();
      ctx.ellipse(
        ((p[0] + p[2]) / 2) * k,
        ((p[1] + p[3]) / 2) * k,
        (Math.abs(p[2] - p[0]) / 2) * k,
        (Math.abs(p[3] - p[1]) / 2) * k,
        0,
        0,
        2 * Math.PI,
      );
      ctx.stroke();
      return;
    case "text": {
      ctx.font = `${h.size * k}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      h.text.split("\n").forEach((line, i) => ctx.fillText(line, p[0] * k, (p[1] + i * h.size * LEADING) * k));
      return;
    }
  }
}

/** A pen's path: a dot for one point, else a curve through the middles of its steps, which smooths the
 * corners of a hand's line. */
function pen(ctx: CanvasRenderingContext2D, p: Int16Array, k: number, width: number): void {
  if (p.length === 2) {
    ctx.beginPath();
    ctx.arc(p[0] * k, p[1] * k, (width / 2) * k, 0, 2 * Math.PI);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(p[0] * k, p[1] * k);
  for (let i = 2; i + 3 < p.length; i += 2)
    ctx.quadraticCurveTo(p[i] * k, p[i + 1] * k, ((p[i] + p[i + 2]) / 2) * k, ((p[i + 1] + p[i + 3]) / 2) * k);
  ctx.lineTo(p[p.length - 2] * k, p[p.length - 1] * k);
  ctx.stroke();
}

/** An arrow's head at `bx`, `by`, pointing away from `ax`, `ay`. */
function head(ctx: CanvasRenderingContext2D, ax: number, ay: number, bx: number, by: number, w: number): void {
  const a = Math.atan2(by - ay, bx - ax);
  const l = Math.max(14, w * 3.5);
  ctx.beginPath();
  for (const s of [-1, 1]) {
    ctx.moveTo(bx, by);
    ctx.lineTo(bx - l * Math.cos(a + (s * Math.PI) / 7), by - l * Math.sin(a + (s * Math.PI) / 7));
  }
  ctx.stroke();
}
